import React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Bold, Italic, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import FontSelector from '@/components/FontSelector';
import ColorInput from '@/components/ColorInput';
import type { TextLayer, TextLayerStyle } from '@/lib/layers/types';

interface Props {
  layer: TextLayer;
  onPatch: (patch: Partial<TextLayer>) => void;
}

/** A labelled group with an on/off switch; children only render when enabled. */
const ToggleSection: React.FC<{
  title: string;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  testId?: string;
  children?: React.ReactNode;
}> = ({ title, enabled, onEnabledChange, testId, children }) => (
  <div className="rounded-lg border bg-muted/30 p-2.5">
    <div className="flex items-center justify-between">
      <Label className="text-xs font-medium">{title}</Label>
      <Switch data-testid={testId} checked={enabled} onCheckedChange={onEnabledChange} />
    </div>
    {enabled && children && <div className="mt-2.5 space-y-2.5">{children}</div>}
  </div>
);

const ColorField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId?: string;
}> = ({ label, value, onChange, testId }) => (
  <div className="flex flex-1 items-center justify-between gap-2 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <ColorInput value={value} onChange={onChange} testId={testId} className="h-7 w-12" />
  </div>
);

const SliderField: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  suffix?: string;
  onChange: (v: number) => void;
  testId?: string;
}> = ({ label, value, min, max, suffix = '', onChange, testId }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{value}{suffix}</span>
    </div>
    <Slider
      data-testid={testId}
      value={[value]}
      min={min}
      max={max}
      step={1}
      onValueChange={([v]) => onChange(v)}
    />
  </div>
);

/** Full text controls for a text layer — the same option set the subtitle
 *  editor offers, minus the per-word concerns (highlight box, current-word
 *  highlight, animation) that need word timing a static layer does not have,
 *  and the layout concerns (margins, position) the layer's `rect` already owns. */
export const TextStyleEditor: React.FC<Props> = ({ layer, onPatch }) => {
  const { t } = useTranslation();
  const s = layer.style;
  const patchStyle = (patch: Partial<TextLayerStyle>) =>
    onPatch({ style: { ...s, ...patch } });

  return (
    <div className="space-y-2.5" data-testid="text-style-editor">
      <div>
        <Label className="text-xs">{t('projectEditor.layers.textContent')}</Label>
        <Textarea
          data-testid="text-style-content"
          value={layer.text}
          rows={2}
          className="mt-1 text-xs"
          onChange={(e) => onPatch({ text: e.target.value, name: e.target.value || layer.name })}
        />
      </div>

      {/* Same picker (and font preview images) the subtitle editor uses.
          fontUrl is persisted alongside the id so the headless render can load
          the font without an API lookup — see TextLayerStyle.fontUrl. */}
      {/* `value` is a backend font id. A layer created before this picker (or a
          brand-new one) holds a built-in AVAILABLE_FONTS *name*, which resolves
          to no option — so show that name as the placeholder rather than
          leaving the field looking unset. It still renders correctly, via
          getFontData's constants branch. */}
      {/* showPreview={false} — as SubtitleEditor does. The preview *card* it
          renders is ~250px tall and overflowed the Style popover; the per-font
          preview images in the dropdown list are unaffected by this flag. */}
      <FontSelector
        value={s.fontName}
        onValueChange={(fontId, font) =>
          patchStyle({ fontName: fontId, fontUrl: font?.file_url ?? null })}
        label={t('projectEditor.layers.font')}
        placeholder={s.fontName}
        showPreview={false}
        className="space-y-1"
      />

      <SliderField
        label={t('projectEditor.layers.fontSize')}
        testId="text-font-size"
        value={s.fontSize}
        min={8}
        max={400}
        onChange={(v) => patchStyle({ fontSize: v })}
      />

      <div className="flex items-center gap-3">
        <ColorField
          label={t('projectEditor.layers.textColor')}
          testId="text-color"
          value={s.color}
          onChange={(v) => patchStyle({ color: v })}
        />
        <div className="flex items-center gap-1">
          <Toggle
            size="sm" pressed={s.bold} aria-label={t('projectEditor.layers.bold')}
            onPressedChange={(v) => patchStyle({ bold: v })}
          >
            <Bold className="h-3 w-3" />
          </Toggle>
          <Toggle
            size="sm" pressed={s.italic} aria-label={t('projectEditor.layers.italic')}
            onPressedChange={(v) => patchStyle({ italic: v })}
          >
            <Italic className="h-3 w-3" />
          </Toggle>
          <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
          {([['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]] as const).map(
            ([align, Icon]) => (
              <Toggle
                key={align} size="sm" pressed={s.align === align}
                aria-label={t(`projectEditor.layers.align${align[0].toUpperCase()}${align.slice(1)}`)}
                onPressedChange={() => patchStyle({ align })}
              >
                <Icon className="h-3 w-3" />
              </Toggle>
            ),
          )}
        </div>
      </div>

      <ToggleSection
        title={t('projectEditor.layers.background')}
        testId="text-background-toggle"
        enabled={s.backgroundColor !== null}
        onEnabledChange={(on) => patchStyle({ backgroundColor: on ? '#000000' : null })}
      >
        <ColorField
          label={t('projectEditor.layers.backgroundColor')}
          testId="text-background-color"
          value={s.backgroundColor ?? '#000000'}
          onChange={(v) => patchStyle({ backgroundColor: v })}
        />
        <SliderField
          label={t('projectEditor.layers.backgroundOpacity')}
          testId="text-background-opacity"
          value={Math.round(s.backgroundOpacity * 100)}
          min={0} max={100} suffix="%"
          onChange={(v) => patchStyle({ backgroundOpacity: v / 100 })}
        />
        <SliderField
          label={t('projectEditor.layers.borderRadius')}
          testId="text-border-radius"
          value={s.borderRadius}
          min={0} max={200}
          onChange={(v) => patchStyle({ borderRadius: v })}
        />
        <div className="flex gap-3">
          <div className="flex-1">
            <SliderField
              label={t('projectEditor.layers.paddingX')}
              testId="text-padding-x"
              value={s.paddingX}
              min={0} max={200}
              onChange={(v) => patchStyle({ paddingX: v })}
            />
          </div>
          <div className="flex-1">
            <SliderField
              label={t('projectEditor.layers.paddingY')}
              testId="text-padding-y"
              value={s.paddingY}
              min={0} max={200}
              onChange={(v) => patchStyle({ paddingY: v })}
            />
          </div>
        </div>
      </ToggleSection>

      <ToggleSection
        title={t('projectEditor.layers.stroke')}
        testId="text-stroke-toggle"
        enabled={s.strokeColor !== null}
        onEnabledChange={(on) =>
          patchStyle(on
            ? { strokeColor: '#000000', strokeWidth: s.strokeWidth || 2 }
            : { strokeColor: null, strokeWidth: 0 })}
      >
        <ColorField
          label={t('projectEditor.layers.strokeColor')}
          testId="text-stroke-color"
          value={s.strokeColor ?? '#000000'}
          onChange={(v) => patchStyle({ strokeColor: v })}
        />
        <SliderField
          label={t('projectEditor.layers.strokeWidth')}
          testId="text-stroke-width"
          value={s.strokeWidth}
          min={0} max={40}
          onChange={(v) => patchStyle({ strokeWidth: v })}
        />
      </ToggleSection>

      <ToggleSection
        title={t('projectEditor.layers.shadow')}
        testId="text-shadow-toggle"
        enabled={s.shadow !== null}
        onEnabledChange={(on) =>
          patchStyle({ shadow: on ? { color: '#000000', blur: 8, x: 0, y: 4 } : null })}
      >
        <ColorField
          label={t('projectEditor.layers.shadowColor')}
          testId="text-shadow-color"
          value={s.shadow?.color ?? '#000000'}
          onChange={(v) => s.shadow && patchStyle({ shadow: { ...s.shadow, color: v } })}
        />
        <SliderField
          label={t('projectEditor.layers.shadowBlur')}
          testId="text-shadow-blur"
          value={s.shadow?.blur ?? 0}
          min={0} max={60}
          onChange={(v) => s.shadow && patchStyle({ shadow: { ...s.shadow, blur: v } })}
        />
        <div className="flex gap-3">
          <div className="flex-1">
            <SliderField
              label={t('projectEditor.layers.shadowOffsetX')}
              testId="text-shadow-x"
              value={s.shadow?.x ?? 0}
              min={-60} max={60}
              onChange={(v) => s.shadow && patchStyle({ shadow: { ...s.shadow, x: v } })}
            />
          </div>
          <div className="flex-1">
            <SliderField
              label={t('projectEditor.layers.shadowOffsetY')}
              testId="text-shadow-y"
              value={s.shadow?.y ?? 0}
              min={-60} max={60}
              onChange={(v) => s.shadow && patchStyle({ shadow: { ...s.shadow, y: v } })}
            />
          </div>
        </div>
      </ToggleSection>
    </div>
  );
};

export default TextStyleEditor;
